import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { QueryKey } from '@tanstack/react-query'

import { API_URL } from '@/constants/env'

import { SweetspotError } from '@/helpers/error'

import { useHandleError } from '@/hooks/common/useErrorHandler'
import { libraryCatalogEntriesQueryKeys } from '@/hooks/library/types'
import { useAuthInfoWithThrow } from '@/hooks/user/useAuthInfoWithThrow'

import type { PaginatedResponse } from '@/types/Api'
import type { UUID } from '@/types/Id'
import type { EntityCatalogEntry } from '@/types/library/Entity'

interface UseDeleteCatalogEntriesArgs {
  orgId: UUID | ''
  schemaId?: UUID
  invalidateActiveQueriesOnSuccess?: boolean
}

interface DeleteCatalogEntriesReq {
  ids: UUID[]
}

const deleteCatalogEntries = async (
  req: DeleteCatalogEntriesReq,
  orgId: UUID | '',
  accessToken: string,
): Promise<void> => {
  if (req.ids.length === 0) {
    return
  }
  if (!orgId) {
    throw new Error('No org id provided.')
  }
  const res = await fetch(
    `${API_URL}/api/v1/organizations/${orgId}/entities/bulk`,
    {
      method: 'DELETE',
      body: JSON.stringify(req.ids),
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
    },
  )
  if (!res.ok) {
    throw new SweetspotError({
      name: 'FETCH_ERROR',
      message: res.statusText,
      cause: res,
    })
  }
}

const getQueryKey = (args: UseDeleteCatalogEntriesArgs): QueryKey => {
  return args.schemaId
    ? libraryCatalogEntriesQueryKeys.schema({
        orgId: args.orgId,
        schemaId: args.schemaId,
      })
    : libraryCatalogEntriesQueryKeys.organization({ orgId: args.orgId })
}

type DeleteCatalogEntriesContext = Array<
  [QueryKey, PaginatedResponse<EntityCatalogEntry> | undefined]
>

export const useDeleteCatalogEntries = ({
  orgId,
  schemaId,
  invalidateActiveQueriesOnSuccess = true,
}: UseDeleteCatalogEntriesArgs) => {
  const { accessToken } = useAuthInfoWithThrow()
  const { handleError } = useHandleError()

  const queryClient = useQueryClient()

  return useMutation<
    void,
    Error,
    DeleteCatalogEntriesReq,
    DeleteCatalogEntriesContext
  >({
    mutationFn: async (req) => {
      await deleteCatalogEntries(req, orgId, accessToken)
    },
    onMutate: async (req) => {
      const prefixKey = getQueryKey({ orgId, schemaId })
      await queryClient.cancelQueries({ queryKey: prefixKey })

      // snapshot all matching queries (by prefix)
      const previousList = queryClient.getQueriesData<
        PaginatedResponse<EntityCatalogEntry>
      >({
        queryKey: prefixKey,
        exact: false,
      })

      // optimistically update each matching query
      for (const [key, data] of previousList) {
        if (data && Array.isArray(data.data)) {
          const remaining = data.data.filter((f) => !req.ids.includes(f.id))
          queryClient.setQueryData<PaginatedResponse<EntityCatalogEntry>>(key, {
            ...data,
            data: remaining,
            count: remaining.length,
            has_more: data.has_more,
          })
        }
      }

      // return rollback values for all updated queries
      return previousList
    },
    onError: (err, req, context) => {
      if (context) {
        for (const [key, previous] of context) {
          queryClient.setQueryData(key, previous)
        }
      }
      handleError(err)
    },
    onSuccess: (data, req) => {
      const prefixKey = getQueryKey({ orgId, schemaId })
      // avoid refetching active queries to avoid triggering loading in the active view
      // (see schema-entries/Table/index.tsx)
      return queryClient.invalidateQueries({
        queryKey: prefixKey,
        exact: false,
        // Only refetch inactive queries to avoid triggering loading in the active view
        refetchType: invalidateActiveQueriesOnSuccess ? 'all' : 'inactive',
      })
    },
  })
}
